import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart, MessageCircle, Sparkles } from 'lucide-react'
import PageHero from '../shared/PageHero'
import { contentApi, GalleryResponse } from '../../services/contentApi'

const GalleryPage = () => {
  const [gallery, setGallery] = useState<GalleryResponse | null>(null)

  useEffect(() => {
    contentApi.getGallery().then(setGallery).catch(() => null)
  }, [])

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero
        eyebrow="Coming soon"
        title="Gallery & devotee testimonials"
        subtitle="A premium space for pilgrim stories, guest photos, and verified experiences — launching soon on Aonla Stay."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="relative overflow-hidden rounded-3xl bg-white border border-gray-200 shadow-sm">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,166,87,0.08),transparent_55%)]" />
          <div className="relative px-6 py-14 sm:px-10 sm:py-16 text-center max-w-2xl mx-auto">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 text-primary-700 mb-6">
              <MessageCircle className="h-8 w-8" />
            </div>
            <p className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-800 mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Testimonials coming soon
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{gallery?.headline || 'Devotee testimonials & photo stories'}</h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              {gallery?.message || 'We are building a trusted gallery where guests share their Manauna Dham journey — honest reviews, photos, and moments from stays booked through Aonla Stay.'}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/rooms" className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors">
                Book a stay first
              </Link>
              <Link to="/contact" className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors">
                Share your story
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2"><Heart className="h-5 w-5 text-primary-500" /> Preview — Manauna Dham</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {(gallery?.preview_images || []).map((img) => (
              <div key={img.src} className="group relative aspect-[4/5] sm:aspect-square rounded-2xl overflow-hidden bg-gray-200">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <p className="absolute bottom-3 left-3 right-3 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">{img.alt}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-gray-500">Guest-submitted testimonials will replace these preview tiles when the gallery launches.</p>
        </div>
      </section>
    </div>
  )
}

export default GalleryPage
